import { useEffect, useState } from "react";

import {
    getAssetById,
} from "../../services/assets/assetService";

import {
    generateAssetLedger,
    verifyLedgerReplaySafety,
} from "../../services/assets/assetLedgerService";

// ── TO-17: Condition badge ────────────────────────────────────────────────────
const CONDITION_UI_AD = {
    WORKING:   { label: "Working",   bg: "#d1fae5", color: "#065f46" },
    IN_REPAIR: { label: "In Repair", bg: "#fef3c7", color: "#92400e" },
    DAMAGED:   { label: "Damaged",   bg: "#fee2e2", color: "#991b1b" },
    LOST:      { label: "Lost",      bg: "#f3f4f6", color: "#374151" },
};
function ConditionBadgeAD({ condition }) {
    const c  = condition || "WORKING";
    const ui = CONDITION_UI_AD[c] || { label: c, bg: "#f3f4f6", color: "#374151" };
    return (
        <span style={{
            background: ui.bg, color: ui.color,
            padding: "4px 12px", borderRadius: "999px",
            fontSize: "13px", fontWeight: "600",
        }}>
            {ui.label}
        </span>
    );
}

export default function AssetDetails() {

    // ==================================================
    // STATE
    // ==================================================

    const [loading, setLoading] =
        useState(false);

    const [error, setError] =
        useState("");

    const [asset, setAsset] =
        useState(null);

    const [ledger, setLedger] =
        useState(null);

    const [replayCheck, setReplayCheck] =
        useState(null);

    // ==================================================
    // USER / COMPANY
    // ==================================================

    const user =
        JSON.parse(
            localStorage.getItem("user") || "{}"
        );

    const company_id =
        localStorage.getItem(
            "company_id"
        ) || user?.company_id;

    // ==================================================
    // URL PARAMS
    // ==================================================

    const params =
        new URLSearchParams(
            window.location.search
        );

    const asset_id =
        params.get(
            "asset_id"
        );

    // ==================================================
    // FORMAT DATE
    // ==================================================

    const formatDate = (
        timestamp
    ) => {

        if (
            !timestamp
        ) {
            return "-";
        }

        try {

            if (
                timestamp?.seconds
            ) {

                return new Date(
                    timestamp.seconds * 1000
                ).toLocaleString();
            }

            return "-";

        } catch {

            return "-";
        }
    };

    // ==================================================
    // LOAD DATA
    // ==================================================

    const loadAssetDetails =
        async () => {

            try {

                if (
                    !asset_id
                ) {

                    setError(
                        "asset_id missing in URL"
                    );

                    return;
                }

                setLoading(true);

                setError("");

                // ======================================
                // LOAD ASSET
                // ======================================

                const assetResponse =
                    await getAssetById(
                        asset_id,
                        company_id
                    );

                setAsset(
                    assetResponse
                );

                // ======================================
                // LOAD LEDGER
                // ======================================

                const ledgerResponse =
                    await generateAssetLedger(
                        asset_id,
                        company_id
                    );

                setLedger(
                    ledgerResponse
                );

                // ======================================
                // VERIFY REPLAY
                // ======================================

                const replayResponse =
                    await verifyLedgerReplaySafety(
                        asset_id,
                        company_id
                    );

                setReplayCheck(
                    replayResponse
                );

            } catch (err) {

                console.error(err);

                setError(
                    err.message ||
                    "Failed to load asset details"
                );

            } finally {

                setLoading(false);
            }
        };

    useEffect(() => {

        loadAssetDetails();

    }, []);

    // ==================================================
    // STATUS COLOR
    // ==================================================

    const getStatusColor = (
        status
    ) => {

        switch (
            status
        ) {

            case "ACTIVE":
                return "#16a34a";

            case "INACTIVE":
                return "#6b7280";

            case "SCRAPPED":
                return "#dc2626";

            case "LOST":
                return "#ea580c";

            default:
                return "#111827";
        }
    };

    // ==================================================
    // TRANSFER STATUS COLOR
    // ==================================================

    const getTransferColor = (
        status
    ) => {

        switch (
            status
        ) {

            case "COMPLETED":
                return "#16a34a";

            case "PENDING_ACCEPTANCE":
                return "#ea580c";

            case "PENDING_STORE_APPROVAL":
                return "#2563eb";

            case "REJECTED":
                return "#dc2626";

            case "CANCELLED":
                return "#6b7280";

            default:
                return "#111827";
        }
    };

    // ==================================================
    // LOADING
    // ==================================================

    if (
        loading
    ) {

        return (

            <div
                style={{
                    padding:
                        "20px",
                }}
            >
                Loading asset details...
            </div>
        );
    }

    // ==================================================
    // ERROR
    // ==================================================

    if (
        error
    ) {

        return (

            <div
                style={{
                    padding:
                        "20px",
                }}
            >

                <div
                    style={{
                        background:
                            "#ffe5e5",

                        color:
                            "#d00000",

                        padding:
                            "14px",

                        borderRadius:
                            "8px",
                    }}
                >
                    {error}
                </div>

            </div>
        );
    }

    // ==================================================
    // NO ASSET
    // ==================================================

    if (
        !asset
    ) {

        return (

            <div
                style={{
                    padding:
                        "20px",
                }}
            >
                Asset not found
            </div>
        );
    }

    // ==================================================
    // UI
    // ==================================================

    return (

        <div
            style={{
                padding:
                    "20px",
            }}
        >

            {/* ====================================== */}
            {/* HEADER */}
            {/* ====================================== */}

            <div
                style={{
                    marginBottom:
                        "20px",
                }}
            >

                <h2>
                    Asset Details
                </h2>

                <p>
                    Replay-safe asset custody information
                </p>

            </div>

            {/* ====================================== */}
            {/* ASSET SUMMARY */}
            {/* ====================================== */}

            <div
                style={{
                    background:
                        "#ffffff",

                    padding:
                        "20px",

                    borderRadius:
                        "12px",

                    marginBottom:
                        "20px",

                    boxShadow:
                        "0 2px 10px rgba(0,0,0,0.08)",
                }}
            >

                <div
                    style={{
                        display:
                            "flex",

                        justifyContent:
                            "space-between",

                        alignItems:
                            "center",

                        marginBottom:
                            "20px",

                        flexWrap:
                            "wrap",

                        gap:
                            "10px",
                    }}
                >

                    <div>

                        <h3
                            style={{
                                margin:
                                    0,
                            }}
                        >

                            {
                                asset.asset_name
                            }

                        </h3>

                        <div
                            style={{
                                color:
                                    "#6b7280",

                                marginTop:
                                    "4px",
                            }}
                        >

                            Asset ID:
                            {" "}

                            {
                                asset.asset_id
                            }

                        </div>

                    </div>

                    <span
                        style={{
                            background:
                                `${getStatusColor(
                                    asset.status
                                )}20`,

                            color:
                                getStatusColor(
                                    asset.status
                                ),

                            padding:
                                "8px 14px",

                            borderRadius:
                                "999px",

                            fontWeight:
                                "600",
                        }}
                    >

                        {
                            asset.status
                        }

                    </span>

                </div>

                {/* DETAILS */}

                <div
                    style={{
                        display:
                            "grid",

                        gridTemplateColumns:
                            "repeat(auto-fit, minmax(220px, 1fr))",

                        gap:
                            "16px",
                    }}
                >

                    <InfoCard
                        label="Category"
                        value={
                            asset.asset_category
                        }
                    />

                    <InfoCard
                        label="Asset Type"
                        value={
                            asset.asset_type
                        }
                    />

                    <InfoCard
                        label="Ownership"
                        value={
                            asset.ownership_mode
                        }
                    />

                    <InfoCard
                        label="Serial Number"
                        value={
                            asset.serial_no
                        }
                    />

                    <InfoCard
                        label="Current Holder"
                        value={`${asset.current_holder_type} / ${asset.current_holder_id}`}
                    />

                    <InfoCard
                        label="Created By"
                        value={
                            asset.created_by
                        }
                    />

                    <InfoCard
                        label="Created At"
                        value={formatDate(
                            asset.created_at
                        )}
                    />

                    <InfoCard
                        label="Remarks"
                        value={
                            asset.remarks
                        }
                    />

                    {/* ── TO-17: Condition ── */}
                    <div
                        style={{
                            background: "#f9fafb",
                            padding: "14px",
                            borderRadius: "10px",
                            border: "1px solid #e5e7eb",
                        }}
                    >
                        <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "8px" }}>
                            Condition
                        </div>
                        <ConditionBadgeAD condition={asset.condition} />
                        {asset.condition_updated_by_name && (
                            <p style={{ fontSize: "11px", color: "#9ca3af", marginTop: "6px" }}>
                                Updated by {asset.condition_updated_by_name}
                                {asset.condition_previous && ` (was ${asset.condition_previous})`}
                            </p>
                        )}
                    </div>

                </div>

            </div>

            {/* ====================================== */}
            {/* REPLAY SAFETY */}
            {/* ====================================== */}

            {replayCheck && (

                <div
                    style={{
                        background:
                            replayCheck.replay_safe

                                ? "#ecfdf5"

                                : "#ffe5e5",

                        border:
                            replayCheck.replay_safe

                                ? "1px solid #10b981"

                                : "1px solid #dc2626",

                        padding:
                            "20px",

                        borderRadius:
                            "12px",

                        marginBottom:
                            "20px",
                    }}
                >

                    <h3
                        style={{
                            marginTop:
                                0,

                            color:
                                replayCheck.replay_safe

                                    ? "#065f46"

                                    : "#991b1b",
                        }}
                    >

                        {replayCheck.replay_safe

                            ? "Replay Safe Verified"

                            : "Replay Mismatch Detected"}
                    </h3>

                    <div
                        style={{
                            display:
                                "grid",

                            gridTemplateColumns:
                                "repeat(auto-fit, minmax(250px, 1fr))",

                            gap:
                                "14px",
                        }}
                    >

                        <InfoCard
                            label="Ledger Holder"
                            value={`${replayCheck.ledger_holder?.holder_type || "-"} / ${replayCheck.ledger_holder?.holder_id || "-"}`}
                        />

                        <InfoCard
                            label="Current Holder"
                            value={`${replayCheck.current_holder?.holder_type || "-"} / ${replayCheck.current_holder?.holder_id || "-"}`}
                        />

                    </div>

                </div>
            )}

            {/* ====================================== */}
            {/* TRANSACTION SUMMARY */}
            {/* ====================================== */}

            {ledger && (

                <div
                    style={{
                        background:
                            "#ffffff",

                        padding:
                            "20px",

                        borderRadius:
                            "12px",

                        marginBottom:
                            "20px",

                        boxShadow:
                            "0 2px 10px rgba(0,0,0,0.08)",
                    }}
                >

                    <h3
                        style={{
                            marginBottom:
                                "16px",
                        }}
                    >
                        Transaction Summary
                    </h3>

                    <div
                        style={{
                            display:
                                "grid",

                            gridTemplateColumns:
                                "repeat(auto-fit, minmax(180px, 1fr))",

                            gap:
                                "16px",
                        }}
                    >

                        <InfoCard
                            label="Total Transactions"
                            value={
                                ledger.total_transactions
                            }
                        />

                        <InfoCard
                            label="Completed"
                            value={
                                ledger.completed_transactions
                            }
                        />

                        <InfoCard
                            label="Pending"
                            value={
                                ledger.pending_transactions
                            }
                        />

                        <InfoCard
                            label="Current Holder"
                            value={`${ledger.current_holder?.holder_type || "-"} / ${ledger.current_holder?.holder_id || "-"}`}
                        />

                    </div>

                </div>
            )}

            {/* ====================================== */}
            {/* TRANSACTION HISTORY */}
            {/* ====================================== */}

            {ledger && (

                <div
                    style={{
                        background:
                            "#ffffff",

                        padding:
                            "20px",

                        borderRadius:
                            "12px",

                        boxShadow:
                            "0 2px 10px rgba(0,0,0,0.08)",
                    }}
                >

                    <h3
                        style={{
                            marginBottom:
                                "16px",
                        }}
                    >
                        Replay Timeline
                    </h3>

                    {ledger.ledger_rows
                        .length ===
                        0 ? (

                        <div>
                            No transactions found
                        </div>

                    ) : (

                        <div
                            style={{
                                overflowX:
                                    "auto",
                            }}
                        >

                            <table
                                width="100%"
                                border="1"
                                cellPadding="10"
                                style={{
                                    borderCollapse:
                                        "collapse",

                                    minWidth:
                                        "1200px",
                                }}
                            >

                                <thead>

                                    <tr
                                        style={{
                                            background:
                                                "#f3f4f6",
                                        }}
                                    >

                                        <th>
                                            #
                                        </th>

                                        <th>
                                            Date
                                        </th>

                                        <th>
                                            Status
                                        </th>

                                        <th>
                                            From
                                        </th>

                                        <th>
                                            To
                                        </th>

                                        <th>
                                            Result Holder
                                        </th>

                                        <th>
                                            Requested
                                        </th>

                                        <th>
                                            Approved
                                        </th>

                                        <th>
                                            Remarks
                                        </th>

                                    </tr>

                                </thead>

                                <tbody>

                                    {ledger.ledger_rows.map(
                                        (
                                            row
                                        ) => (

                                            <tr
                                                key={
                                                    row.transaction_id
                                                }
                                            >

                                                <td>
                                                    {
                                                        row.sequence
                                                    }
                                                </td>

                                                <td>
                                                    {formatDate(
                                                        row.created_at
                                                    )}
                                                </td>

                                                <td>

                                                    <span
                                                        style={{
                                                            background:
                                                                `${getTransferColor(
                                                                    row.status
                                                                )}20`,

                                                            color:
                                                                getTransferColor(
                                                                    row.status
                                                                ),

                                                            padding:
                                                                "4px 10px",

                                                            borderRadius:
                                                                "999px",

                                                            fontSize:
                                                                "12px",

                                                            fontWeight:
                                                                "600",
                                                        }}
                                                    >

                                                        {
                                                            row.status
                                                        }

                                                    </span>

                                                </td>

                                                <td>

                                                    {
                                                        row.from_type
                                                    }

                                                    {" / "}

                                                    {
                                                        row.from_id
                                                    }

                                                </td>

                                                <td>

                                                    {
                                                        row.to_type
                                                    }

                                                    {" / "}

                                                    {
                                                        row.to_id
                                                    }

                                                </td>

                                                <td>

                                                    {
                                                        row.resulting_holder
                                                            ?.holder_type
                                                    }

                                                    {" / "}

                                                    {
                                                        row.resulting_holder
                                                            ?.holder_id
                                                    }

                                                </td>

                                                <td>
                                                    {
                                                        row.requested_by
                                                    }
                                                </td>

                                                <td>
                                                    {
                                                        row.approved_by ||
                                                        "-"
                                                    }
                                                </td>

                                                <td>
                                                    {
                                                        row.remarks ||
                                                        "-"
                                                    }
                                                </td>

                                            </tr>
                                        )
                                    )}

                                </tbody>

                            </table>

                        </div>
                    )}

                </div>
            )}

        </div>
    );
}

// ======================================================
// INFO CARD
// ======================================================

function InfoCard({
    label,
    value,
}) {

    return (

        <div
            style={{
                background:
                    "#f9fafb",

                padding:
                    "14px",

                borderRadius:
                    "10px",

                border:
                    "1px solid #e5e7eb",
            }}
        >

            <div
                style={{
                    fontSize:
                        "12px",

                    color:
                        "#6b7280",

                    marginBottom:
                        "5px",
                }}
            >
                {label}
            </div>

            <div
                style={{
                    fontWeight:
                        "600",

                    wordBreak:
                        "break-word",
                }}
            >
                {value || "-"}
            </div>

        </div>
    );
}